import type { SensorAlertBundle } from '@mark43/cad-api';

// Dependency-free on purpose: the action broadcaster's own types import this, and anything heavier
// here would close an import cycle through the broadcaster.

/**
 * Actions that open a sensor surface in the map window.
 *
 * Camera and sensor event panels only live over the map, so a surface in another window — the
 * event panel's Sensor Events tab, in the events window — has to ask the map window to open them
 * rather than opening them where it is.
 *
 * The sensor event action carries the whole alert, not just its key: the map window's store may
 * not hold an alert the event panel knows about (an old link, hydrated from the event), and the
 * panel reads its alert from that store.
 *
 * `centerMap` also moves the map to the camera. Only the alert toast's button asks for it: the map
 * never moves on its own when a sensor event arrives.
 */
export type SensorPanelMapAction =
    | { type: 'OPEN_SENSOR_EVENT_PANEL'; payload: { alert: SensorAlertBundle } }
    | { type: 'OPEN_SENSOR_PANEL'; payload: { sensorId: string; centerMap?: boolean } };

const SENSOR_PANEL_MAP_ACTION_TYPES: ReadonlySet<string> = new Set([
    'OPEN_SENSOR_EVENT_PANEL',
    'OPEN_SENSOR_PANEL',
]);

export function isSensorPanelMapAction(action: { type: string }): action is SensorPanelMapAction {
    return SENSOR_PANEL_MAP_ACTION_TYPES.has(action.type);
}
